/** @fileoverview Reads every chart of one screen in a single call, after the list filters settle, and shares the answers with the chart blocks. */

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useStore } from 'zustand';

import { stableStringify } from '../../lib/utils';

import type {
  FabrikChartComponent,
  FabrikChartResult,
  FabrikComponent,
  FabrikListComponent,
  FabrikListFilter,
  FabrikListFilterChoices,
  FabrikRecordKeyRefValue,
  InputSnapshot,
} from '../../types/apps';
import { useComponentState, useDataVersion } from './appRuntimeContext';
import { fieldsForRecordKey } from './helpers';
import { toListFilterChoices, useListFilterStore } from './listFilterStore';
import { workdayRefsOf } from './workdaySource';
import type { FieldValues } from './types';

/** How long the filters must stay the same before the charts are read again. */
const FILTER_SETTLE_MS = 500;

/** What the chart blocks of a screen know about their numbers. */
export type ScreenChartsValue = {
  /** False in the builder before the app is saved; the charts then show a note. */
  isAvailable: boolean;
  /** True while a read runs. Answers of the read before stay in `results` meanwhile. */
  isLoading: boolean;
  /** True once the first read has finished, with answers or with an error. */
  hasLoaded: boolean;
  /** Answers by chart id. */
  results: Record<string, FabrikChartResult>;
  /** Set when the whole read failed, so no chart has an answer. */
  error: string;
};

const NO_RESULTS: Record<string, FabrikChartResult> = {};

const ScreenChartsContext = createContext<ScreenChartsValue>({
  isAvailable: false,
  isLoading: false,
  hasLoaded: false,
  results: NO_RESULTS,
  error: '',
});

/** Returns what the screen's charts read has answered so far. */
export const useScreenCharts = () => useContext(ScreenChartsContext);

/**
 * Lists the charts and lists of a screen, including the ones inside Grid and Card.
 * @param components the screen's top-level components
 */
const chartsAndListsOf = (components: FabrikComponent[]) => {
  const charts: FabrikChartComponent[] = [];
  const lists: FabrikListComponent[] = [];
  /**
   * Collects one component, or the components inside a Grid or Card.
   * @param component the component to look at
   */
  const visit = (component: FabrikComponent) => {
    if (component.kind === 'grid') {
      component.items.forEach(({ component: child }) => visit(child));
    } else if (component.kind === 'card') {
      visit(component.content);
    } else if (component.kind === 'chart') {
      charts.push(component);
    } else if (component.kind === 'list') {
      lists.push(component);
    }
  };
  components.forEach(visit);
  return { charts, lists };
};

/**
 * Lists the refs the charted lists' primary sources read from the screen: row filter values,
 * the inputs a row filter's `when` reads, and effective date days. The server reads only these
 * from the record, the globals and the inputs.
 * @param charts the screen's charts
 * @param lists the screen's lists
 */
const chartRefsOf = (
  charts: FabrikChartComponent[],
  lists: FabrikListComponent[]
): FabrikRecordKeyRefValue[] => {
  const chartLists = new Set(charts.map(({ from }) => from));
  return lists.flatMap(({ id, dataSource, primaryDataSourceId }) => {
    const source = dataSource.find((candidate) => candidate.id === primaryDataSourceId);
    if (!chartLists.has(id) || !source || source.type === 'items') {
      return [];
    }
    if (source.type === 'workday') {
      return workdayRefsOf(source);
    }
    const { rowFilters = [], effectiveDates } = source;
    const filterRefs = rowFilters.flatMap(({ value, when }) =>
      when ? [value, when.value] : [value]
    );
    const days = !effectiveDates
      ? []
      : 'asOf' in effectiveDates
      ? [effectiveDates.asOf]
      : [effectiveDates.from, effectiveDates.to];
    const dayRefs = days.filter(
      (day): day is FabrikRecordKeyRefValue => typeof day === 'object' && day !== null
    );
    return [...filterRefs, ...dayRefs];
  });
};

/**
 * Keeps only the inputs the refs read. A standalone input is kept alone; a form is kept whole,
 * because the server refuses a form that is missing one of its required inputs.
 * @param inputs every input of the screen
 * @param refs the refs the charted lists read
 */
const inputsForRefs = (inputs: InputSnapshot, refs: FabrikRecordKeyRefValue[]): InputSnapshot => {
  const { standalone = {}, forms = {} } = inputs;
  const inputRefs = refs.filter(
    (ref): ref is Extract<FabrikRecordKeyRefValue, { scope: 'inputs' }> => ref.scope === 'inputs'
  );
  const keptStandalone = Object.fromEntries(
    inputRefs.flatMap(({ formId, field }) =>
      formId === undefined && Object.hasOwn(standalone, field) ? [[field, standalone[field]]] : []
    )
  );
  const keptForms = Object.fromEntries(
    inputRefs.flatMap(({ formId }) =>
      formId !== undefined && Object.hasOwn(forms, formId) ? [[formId, forms[formId]]] : []
    )
  );
  return {
    ...(Object.keys(keptStandalone).length ? { standalone: keptStandalone } : {}),
    ...(Object.keys(keptForms).length ? { forms: keptForms } : {}),
  };
};

/**
 * Text that changes only when a value the refs read changes, so typing in an input or form
 * field the charts do not read does not read the charts again.
 * @param inputs every input of the screen
 * @param refs the refs the charted lists read
 */
const readInputsKeyOf = (inputs: InputSnapshot, refs: FabrikRecordKeyRefValue[]): string =>
  stableStringify(
    refs.flatMap((ref) => {
      if (ref.scope !== 'inputs') {
        return [];
      }
      const { formId, field } = ref;
      return [
        [
          formId ?? '',
          field,
          formId === undefined ? inputs.standalone?.[field] : inputs.forms?.[formId]?.[field],
        ],
      ];
    })
  );

type ScreenChartsProviderProps = {
  screenId: string;
  components: FabrikComponent[];
  fields: FieldValues;
  inputs: InputSnapshot;
  children: ReactNode;
};

/**
 * Sends one charts read for the whole screen when it has charts. The first read goes at once;
 * after that, a change of filters, record or inputs waits until nothing changed for 500 ms. A
 * newer read, or leaving the screen, stops the read before it. A write that changes data reads
 * the charts again. Must sit inside the screen's list filter store.
 * @param props the screen id, its components, the record fields, the inputs and the screen
 */
export default function ScreenChartsProvider({
  screenId,
  components,
  fields,
  inputs,
  children,
}: ScreenChartsProviderProps) {
  const componentState = useComponentState();
  const dataVersion = useDataVersion();
  const filterStore = useListFilterStore();
  const valuesByList = useStore(filterStore, (state) => state.valuesByList);
  const { charts, lists } = useMemo(() => chartsAndListsOf(components), [components]);
  const hasCharts = charts.length > 0;

  /* Only the lists a chart reads send their chips. */
  const filtersByList = useMemo(() => {
    const chartLists = new Set(charts.map(({ from }) => from));
    return Object.fromEntries(
      lists.flatMap(({ id, filters }): [string, FabrikListFilter[]][] =>
        chartLists.has(id) ? [[id, filters ?? []]] : []
      )
    );
  }, [charts, lists]);
  const listFiltersKey = useMemo(
    () => stableStringify(toListFilterChoices(valuesByList, filtersByList)),
    [filtersByList, valuesByList]
  );
  /* Only what the charted lists read is sent and watched, so other typing reads nothing. */
  const refsKey = useMemo(() => stableStringify(chartRefsOf(charts, lists)), [charts, lists]);
  const refs = useMemo(() => JSON.parse(refsKey) as FabrikRecordKeyRefValue[], [refsKey]);
  const fieldsKey = useMemo(
    () => stableStringify(fieldsForRecordKey(fields, refs)),
    [fields, refs]
  );
  const inputsKey = useMemo(() => readInputsKeyOf(inputs, refs), [inputs, refs]);
  /* The whole forms a read sends come from the latest inputs when it is sent. */
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;
  /* A definition edit that changes a chart reads again; one that keeps them does not. */
  const chartsKey = useMemo(() => stableStringify(charts), [charts]);

  const [state, setState] = useState<Omit<ScreenChartsValue, 'isAvailable'>>({
    isLoading: false,
    hasLoaded: false,
    results: NO_RESULTS,
    error: '',
  });
  const hasSentRef = useRef(false);

  useEffect(() => {
    if (!hasCharts || !componentState) {
      return undefined;
    }
    const controller = new AbortController();
    setState((current) => ({ ...current, isLoading: true }));
    /** Sends the read and keeps its answers, unless a newer read replaced it. */
    const send = () => {
      hasSentRef.current = true;
      componentState
        .readCharts({
          screenId,
          fields: JSON.parse(fieldsKey) as FieldValues,
          inputs: inputsForRefs(inputsRef.current, refs),
          recordKey: refs,
          listFilters: JSON.parse(listFiltersKey) as FabrikListFilterChoices,
          signal: controller.signal,
        })
        .then(({ charts: answers }) => {
          if (controller.signal.aborted) {
            return;
          }
          const results = Object.fromEntries(
            (Array.isArray(answers) ? answers : []).map((answer) => [answer.id, answer])
          );
          setState({ isLoading: false, hasLoaded: true, results, error: '' });
        })
        .catch((reason: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          setState({
            isLoading: false,
            hasLoaded: true,
            results: NO_RESULTS,
            error: reason instanceof Error ? reason.message : 'Unable to load the charts.',
          });
        });
    };
    const timer = setTimeout(send, hasSentRef.current ? FILTER_SETTLE_MS : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    chartsKey,
    componentState,
    dataVersion,
    fieldsKey,
    hasCharts,
    inputsKey,
    listFiltersKey,
    refs,
    screenId,
  ]);

  const value = useMemo(
    () => ({ ...state, isAvailable: Boolean(componentState) }),
    [componentState, state]
  );

  /* Always the same tree, so adding the first chart does not remount the screen. */
  return <ScreenChartsContext.Provider value={value}>{children}</ScreenChartsContext.Provider>;
}
